import { FormEvent, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useFlagSearch } from '../FeatureFlagsContext';
import { useQuestGlitch } from '../QuestGlitchContext';
import { getQuestPageConfig, splitQuestIntroParagraphs, type QuestCodeResult } from '../questPages';
import { getQuestDissolveMs, prefersReducedQuestMotion } from '../questTransition';

type QuestCodeError = Extract<QuestCodeResult, { kind: 'error' }>;

export function QuestPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const flagSearch = useFlagSearch();
  const { startQuestTransition } = useQuestGlitch();
  const config = getQuestPageConfig(location.pathname);
  const [code, setCode] = useState('');
  const [result, setResult] = useState<QuestCodeError | null>(null);
  const [introVisible, setIntroVisible] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const exitTimeoutRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    setCode('');
    setResult(null);
    setIsExiting(false);
    setIntroVisible(false);
    setPanelVisible(false);

    const pageConfig = getQuestPageConfig(location.pathname);
    if (!pageConfig) {
      return undefined;
    }

    const reducedMotion = prefersReducedQuestMotion();
    const panelDelay = reducedMotion ? 0 : pageConfig.showModal ? pageConfig.panelRevealMs : 0;

    const introTimer = window.setTimeout(() => {
      if (pageConfig.introText) {
        setIntroVisible(true);
      }
    }, 0);

    const panelTimer = window.setTimeout(() => {
      if (pageConfig.showModal) {
        setPanelVisible(true);
      }
    }, panelDelay);

    return () => {
      window.clearTimeout(introTimer);
      window.clearTimeout(panelTimer);
    };
  }, [location.pathname]);

  useLayoutEffect(() => {
    return () => {
      if (exitTimeoutRef.current !== null) {
        window.clearTimeout(exitTimeoutRef.current);
        exitTimeoutRef.current = null;
      }
    };
  }, []);

  if (!config) {
    return <Navigate to={{ pathname: '/', search: flagSearch }} replace />;
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isExiting) return;

    const outcome = config.handleCode(code);
    if (outcome.kind === 'success') {
      const isExternal = /^https?:\/\//.test(outcome.redirectTo);

      if (config.glitchBeforeRedirect) {
        setIsExiting(true);
        startQuestTransition(outcome.redirectTo);
      } else {
        setIsExiting(true);
        setIntroVisible(false);
        setPanelVisible(false);
        exitTimeoutRef.current = window.setTimeout(() => {
          exitTimeoutRef.current = null;
          if (isExternal) {
            window.location.href = outcome.redirectTo;
          } else {
            navigate({ pathname: outcome.redirectTo, search: flagSearch }, { replace: true });
          }
        }, getQuestDissolveMs());
      }
      return;
    }
    setResult(outcome);
  };

  const interactionsDisabled = isExiting || !panelVisible;

  return (
    <main
      className={`quest${config.showHeader ? '' : ' quest--no-header'}${config.showFooter ? '' : ' quest--no-footer'}`}
    >
      {config.introText && (
        <div
          className={`quest__intro quest__intro--${config.introTextAlign} quest__dissolve${introVisible ? ' quest__dissolve--visible' : ''}`}
        >
          {splitQuestIntroParagraphs(config.introText).map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
      )}

      {config.showModal && config.modal && (
        <div
          className={`quest__inner quest__dissolve${panelVisible ? ' quest__dissolve--visible' : ''}`}
          inert={interactionsDisabled}
          aria-hidden={interactionsDisabled}
        >
          <form className="quest__form" onSubmit={submit}>
            <p className="quest__prompt">{config.modal.prompt}</p>
            <label className="quest__label" htmlFor={config.modal.inputId}>
              {config.modal.inputLabel}
            </label>
            <input
              id={config.modal.inputId}
              name="code"
              type="text"
              className="quest__input"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={interactionsDisabled}
            />
            <button
              type="submit"
              className="quest__btn quest__btn--primary"
              disabled={interactionsDisabled}
            >
              {config.modal.submitLabel}
            </button>
            {result?.kind === 'error' && (
              <p className="quest__feedback quest__feedback--error" role="status">
                {result.message}
              </p>
            )}
          </form>
        </div>
      )}
    </main>
  );
}
